import { Navigate, Link } from "react-router-dom";
import {
    Phone, MapPin, ArrowRight, CheckCircle2, Star, Award, Users, Clock,
    Video, GraduationCap, ShieldCheck, MessageCircle,
} from "lucide-react";
import PageHeader from "../components/PageHeader";
import RegistrationForm from "../components/RegistrationForm";
import SEO from "../components/SEO";
import { QuickAnswerCard, ConversationalFAQ, buildConversationalFAQSchema } from "../components/AEOContent";
import { buildOnlineCityAEO } from "../data/aeoContent";
import { EXTENDED_CITIES, EXTENDED_CITY_GEO, ENQUIRY_PHONE, ENQUIRY_PHONE_RAW } from "../data/extendedCities";
import { TIER2_CITIES, TIER2_CITY_GEO } from "../data/tier2Cities";
import { TIER3_CITIES_BATCH_2A } from "../data/tier3CitiesBatch2A";
import { BOARDS, COURSES, SOCIAL } from "../data/siteData";

// Additive merge — Phase-1 Tier-2 city entries live in a separate module so
// existing extendedCities.js remains 100% untouched. Both maps are unioned at
// lookup time only; existing 9 city URLs and SEO are unaffected.
const ALL_CITIES = { ...EXTENDED_CITIES, ...TIER2_CITIES, ...TIER3_CITIES_BATCH_2A };
const ALL_CITY_GEO = { ...EXTENDED_CITY_GEO, ...TIER2_CITY_GEO };

export default function OnlineCity({ citySlug }) {
    const data = ALL_CITIES[citySlug];
    if (!data) return <Navigate to="/" replace />;

    const geo = ALL_CITY_GEO[citySlug] || null;
    const canonicalPath = `/${data.urlSlug}`;
    const canonicalUrl = `https://anandtutorials.com${canonicalPath}`;

    // Batch 2N (Feb-2026) — city AEO (QuickAnswer + Speakable FAQ + FAQPage schema)
    const cityAeo = buildOnlineCityAEO({ name: data.name, state: data.state, slug: data.slug });
    const aeoFaqSchema = buildConversationalFAQSchema(cityAeo.faqs);

    // 1) LocalBusiness + EducationalOrganization (with geo when available)
    // NOTE: This city page is served purely via 1:1 Live Online Classes — Anand
    // Tutorials' offline / in-home tutor network is strictly restricted to
    // Jabalpur, Bhopal, Nagpur (handled by CityLanding.jsx).
    const localBusinessSchema = {
        "@context": "https://schema.org",
        "@type": ["EducationalOrganization", "OnlineBusiness"],
        "name": `Anand Tutorials — 1:1 Online Classes in ${data.name}`,
        "alternateName": [
            `Anand Tutorials ${data.name}`,
            `1:1 Online Tuition in ${data.name}`,
            `Online Tutors in ${data.name}`,
            `Live Online Classes in ${data.name}`,
        ],
        "image": "https://anandtutorials.com/logo-anand-tutorials.png",
        "logo": "https://anandtutorials.com/logo-anand-tutorials.png",
        "@id": `${canonicalUrl}#localbusiness`,
        "url": canonicalUrl,
        "telephone": ENQUIRY_PHONE,
        "email": "anandhometutorials@gmail.com",
        "branchOf": {
            "@type": "EducationalOrganization",
            "name": "Anand Tutorials",
            "url": "https://anandtutorials.com",
        },
        "knowsAbout": [
            `1:1 Online Tuition in ${data.name}`,
            `Online Tutor in ${data.name}`,
            `Live Online Classes in ${data.name}`,
            `CBSE Online Tutor in ${data.name}`,
            `ICSE Online Tutor in ${data.name}`,
            `NEET Online Coaching in ${data.name}`,
            `JEE Online Coaching in ${data.name}`,
            `Private Online Tutor in ${data.name}`,
        ],
        "areaServed": {
            "@type": "City",
            "name": data.name,
            "containedInPlace": { "@type": "AdministrativeArea", "name": data.state },
        },
        "address": {
            "@type": "PostalAddress",
            "addressLocality": data.name,
            "addressRegion": data.state,
            "addressCountry": geo ? geo.countryCode : "IN",
        },
        ...(geo ? {
            "geo": { "@type": "GeoCoordinates", "latitude": geo.lat, "longitude": geo.lng },
        } : {}),
        "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": data.rating.value,
            "reviewCount": data.rating.count.replace(/,/g, ""),
            "bestRating": "5",
            "worstRating": "1",
        },
        "openingHoursSpecification": [{
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
            "opens": "09:00",
            "closes": "21:00",
        }],
        "sameAs": [SOCIAL.facebook, SOCIAL.instagram].filter(Boolean),
    };

    // 2) Organisation (brand root)
    const organizationSchema = {
        "@context": "https://schema.org",
        "@type": "EducationalOrganization",
        "@id": "https://anandtutorials.com#organization",
        "name": "Anand Tutorials",
        "url": "https://anandtutorials.com",
        "logo": "https://anandtutorials.com/logo-anand-tutorials.png",
        "foundingDate": "2013",
    };

    // 3) WebPage
    const webPageSchema = {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "@id": `${canonicalUrl}#webpage`,
        "url": canonicalUrl,
        "name": data.seoTitle,
        "description": data.seoDescription,
        "inLanguage": "en-IN",
        "isPartOf": { "@id": "https://anandtutorials.com#organization" },
        "about": { "@id": `${canonicalUrl}#localbusiness` },
        "breadcrumb": { "@id": `${canonicalUrl}#breadcrumb` },
    };

    // 4) BreadcrumbList — Home › Online Classes › <City>
    const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "@id": `${canonicalUrl}#breadcrumb`,
        "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://anandtutorials.com/" },
            { "@type": "ListItem", "position": 2, "name": "1:1 Online Classes", "item": "https://anandtutorials.com/online-classes" },
            { "@type": "ListItem", "position": 3, "name": data.name, "item": canonicalUrl },
        ],
    };

    // 5) Service offered in this city — 1:1 Live Online Classes only
    const serviceSchema = {
        "@context": "https://schema.org",
        "@type": "Service",
        "@id": `${canonicalUrl}#service`,
        "serviceType": `1:1 Live Online Tuition in ${data.name}`,
        "provider": { "@id": "https://anandtutorials.com#organization" },
        "areaServed": {
            "@type": "City",
            "name": data.name,
            "containedInPlace": { "@type": "AdministrativeArea", "name": data.state },
        },
        "audience": { "@type": "PeopleAudience", "audienceType": "Students Grade 1-12 + NEET / JEE aspirants" },
        "category": "Online Educational Services",
        "url": canonicalUrl,
    };

    // 6) FAQPage
    const faqSchema = data.faqs && data.faqs.length ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "@id": `${canonicalUrl}#faq`,
        "mainEntity": data.faqs.map(([q, a]) => ({
            "@type": "Question",
            "name": q,
            "acceptedAnswer": { "@type": "Answer", "text": a },
        })),
    } : null;

    // 7) Course — a top-level 1:1 Online Tuition course for this city.
    // Emits pricing-free InStock free-demo Offer + AggregateRating for LLM
    // extraction alongside the existing Service block.
    const courseSchema = {
        "@context": "https://schema.org",
        "@type": "Course",
        "@id": `${canonicalUrl}#course`,
        "name": `1:1 Live Online Tuition — ${data.name}`,
        "description": data.seoDescription,
        "url": canonicalUrl,
        "provider": { "@id": "https://anandtutorials.com#organization", "name": "Anand Tutorials", "sameAs": "https://anandtutorials.com" },
        "audience": {
            "@type": "EducationalAudience",
            "educationalRole": "student",
            "geographicArea": { "@type": "City", "name": data.name },
        },
        "inLanguage": "en",
        "hasCourseInstance": [{
            "@type": "CourseInstance",
            "courseMode": "online",
            "inLanguage": "en",
            "name": `1:1 Live Online Tuition · ${data.name}`,
            "location": { "@type": "VirtualLocation", "url": canonicalUrl },
            "offers": [{
                "@type": "Offer",
                "availability": "https://schema.org/InStock",
                "price": "0",
                "priceCurrency": "INR",
                "category": "Free demo class",
                "url": "https://anandtutorials.com/contact",
            }],
        }],
        "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": data.rating?.value || "4.9",
            "reviewCount": String(data.rating?.count || "500").replace(/,/g, ""),
            "bestRating": "5",
            "worstRating": "1",
        },
    };

    return (
        <div data-testid={`online-city-${data.slug}`}>
            <SEO
                title={data.seoTitle}
                description={data.seoDescription}
                keywords={data.keywords}
                path={canonicalPath}
                canonicalPath={canonicalPath}
                geo={geo ? {
                    region: geo.regionCode,
                    placename: `${data.name}, ${data.state}`,
                    lat: geo.lat,
                    lng: geo.lng,
                } : undefined}
                schemas={[
                    localBusinessSchema,
                    organizationSchema,
                    webPageSchema,
                    breadcrumbSchema,
                    serviceSchema,
                    courseSchema,
                    faqSchema,
                    // Batch 2N — city AEO FAQPage schema
                    aeoFaqSchema,
                ]}
            />

            <PageHeader
                eyebrow={`1:1 Online Classes · ${data.state}`}
                title={data.h1}
                subtitle={data.intro}
            >
                <div className="flex flex-wrap gap-3">
                    <a href={`tel:${ENQUIRY_PHONE_RAW}`} data-testid={`oc-${data.slug}-call`} className="inline-flex items-center gap-2 bg-amber-500 text-[#0F172A] font-semibold px-5 py-3 rounded-lg hover:bg-amber-400 transition-colors">
                        <Phone className="w-4 h-4" /> Call India Team
                    </a>
                    <Link to="/register" data-testid={`oc-${data.slug}-demo`} className="inline-flex items-center gap-2 bg-white/10 border border-white/20 backdrop-blur text-white font-semibold px-5 py-3 rounded-lg hover:bg-white/15">
                        Book Free Demo <ArrowRight className="w-4 h-4" />
                    </Link>
                    <a href={`https://wa.me/${SOCIAL.whatsapp}?text=${encodeURIComponent(`Hi Anand Tutorials, I am from ${data.name} and would like 1:1 online tuition.`)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-[#25D366] text-white font-semibold px-5 py-3 rounded-lg hover:bg-[#1ebe5a]">
                        <MessageCircle className="w-4 h-4" /> WhatsApp
                    </a>
                </div>
            </PageHeader>

            {/* Batch 2N — City AEO Quick-Answer + Speakable FAQ */}
            <QuickAnswerCard
                question={cityAeo.quickAnswer.question}
                answer={cityAeo.quickAnswer.answer}
                bullets={cityAeo.quickAnswer.bullets}
                footer={cityAeo.quickAnswer.footer}
                testId={`city-quick-answer-${data.slug}`}
            />

            {/* Stats trust strip */}
            <section className="bg-white border-b border-slate-200">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                    {[
                        { i: Users, n: "50,000+", l: "Students taught" },
                        { i: Star, n: `${data.rating.value} / 5`, l: `${data.rating.count} ${data.name} parents` },
                        { i: Award, n: "98%", l: "Pass rate (boards)" },
                        { i: Clock, n: "24 hrs", l: "Tutor match time" },
                    ].map(({ i: I, n, l }) => (
                        <div key={l}>
                            <I className="w-6 h-6 text-[#1E3A8A] mx-auto mb-2" />
                            <div className="font-heading text-2xl sm:text-3xl font-bold text-[#0F172A]">{n}</div>
                            <div className="text-xs text-slate-500 mt-0.5">{l}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* SEO sections */}
            <section className="py-16 bg-white">
                <div className="max-w-4xl mx-auto px-6 lg:px-8 space-y-10">
                    {data.sections.map((s, i) => (
                        <article key={i}>
                            <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-4 text-[#0F172A]">{s.h2}</h2>
                            <p className="text-slate-700 leading-relaxed text-base sm:text-lg">{s.p}</p>
                        </article>
                    ))}
                </div>
            </section>

            {/* Services offered */}
            <section className="py-16 bg-[#F8FAFC]">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#3B82F6] mb-3">Programs in {data.name}</div>
                    <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-8">1:1 Live Online Classes — as personal as a home tutor, without the commute.</h2>
                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="bg-white border border-slate-200 rounded-2xl p-6 hover:border-blue-200 transition-colors">
                            <div className="w-12 h-12 rounded-xl bg-blue-50 text-[#1E3A8A] flex items-center justify-center mb-4">
                                <Video className="w-6 h-6" />
                            </div>
                            <h3 className="font-heading text-xl font-semibold mb-2">1:1 Live Online Classes in {data.name}</h3>
                            <p className="text-slate-600 leading-relaxed text-sm mb-3">Live two-way HD video on Zoom or Google Meet. One student, one tutor, one personalised plan — recorded for revision, with weekly Sunday parent reports.</p>
                            <ul className="space-y-1.5 text-sm text-slate-600">
                                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" /> All boards & subjects covered</li>
                                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" /> Free demo before any payment</li>
                                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" /> Flexible slots — including weekends</li>
                            </ul>
                            <Link to="/online-classes" className="mt-4 inline-flex items-center gap-1 text-[#1E3A8A] font-semibold text-sm hover:gap-2 transition-all">
                                Learn more about online classes <ArrowRight className="w-4 h-4" />
                            </Link>
                        </div>
                        <div className="bg-white border border-slate-200 rounded-2xl p-6 hover:border-blue-200 transition-colors">
                            <div className="w-12 h-12 rounded-xl bg-blue-50 text-[#1E3A8A] flex items-center justify-center mb-4">
                                <MapPin className="w-6 h-6" />
                            </div>
                            <h3 className="font-heading text-xl font-semibold mb-2">Prefer an in-home tutor?</h3>
                            <p className="text-slate-600 leading-relaxed text-sm mb-3">Anand Tutorials operates in-home visits only in Jabalpur, Bhopal and Nagpur. For every other city — including {data.name} — our 1:1 live online classes deliver the same personal attention with zero commute.</p>
                            <ul className="space-y-1.5 text-sm text-slate-600">
                                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" /> Same tutor every session</li>
                                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" /> Same personalised plan</li>
                                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" /> Zero travel — more study time</li>
                            </ul>
                            <div className="mt-4 flex flex-wrap gap-3 text-sm">
                                <Link to="/jabalpur" className="inline-flex items-center gap-1 text-[#1E3A8A] font-semibold hover:gap-2 transition-all">Home Tuition · Jabalpur <ArrowRight className="w-4 h-4" /></Link>
                                <Link to="/bhopal" className="inline-flex items-center gap-1 text-[#1E3A8A] font-semibold hover:gap-2 transition-all">Bhopal <ArrowRight className="w-4 h-4" /></Link>
                                <Link to="/nagpur" className="inline-flex items-center gap-1 text-[#1E3A8A] font-semibold hover:gap-2 transition-all">Nagpur <ArrowRight className="w-4 h-4" /></Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Areas served */}
            <section className="py-16 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#3B82F6] mb-3">Areas We Serve Online</div>
                    <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-3">Live 1:1 online tutors for every {data.name} neighbourhood</h2>
                    <p className="text-slate-600 mb-8 max-w-3xl">Our verified online tutors serve every major neighbourhood in {data.name} — anywhere with an internet connection. If your area isn't listed, call us — we already cover it.</p>
                    <div className="grid sm:grid-cols-3 md:grid-cols-5 gap-3">
                        {data.areas.map((a) => (
                            <div key={a} className="bg-[#F8FAFC] border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-700 flex items-center gap-2">
                                <CheckCircle2 className="w-4 h-4 text-amber-500 shrink-0" />
                                <span>Online tuition in {a}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Popular courses in this city — Batch 2I reverse cross-links */}
            {data.relatedCourses && data.relatedCourses.length > 0 && (
                <section className="py-16 bg-white border-t border-slate-100" data-testid={`city-${data.slug}-related-courses`}>
                    <div className="max-w-7xl mx-auto px-6 lg:px-8">
                        <div className="text-xs font-semibold uppercase tracking-[0.3em] text-emerald-600 mb-3">Popular in {data.name}</div>
                        <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0F172A] mb-3">The courses & exam tracks {data.name} parents ask for most.</h2>
                        <p className="text-slate-600 mb-8 max-w-3xl">
                            Each course pillar below is our dedicated 1:1 mentor track — every tutor in it is board-code-matched, syllabus-fluent and rate-transparent. Tap any card to see the exam-pattern deep-dive, sample outcomes, mid-year plan and Q&A for that track.
                        </p>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {data.relatedCourses.map((rc, i) => (
                                <Link
                                    key={rc.path}
                                    to={rc.path}
                                    data-testid={`city-${data.slug}-course-${i}`}
                                    className="group flex items-start gap-3 p-5 rounded-xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 hover:border-emerald-500 hover:shadow-sm transition-all"
                                >
                                    <div className="w-9 h-9 rounded-lg bg-emerald-600/10 text-emerald-700 flex items-center justify-center shrink-0 group-hover:bg-emerald-600 group-hover:text-white transition-colors">
                                        <GraduationCap className="w-5 h-5" />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="font-heading font-semibold text-[#0F172A] text-sm mb-1 group-hover:text-emerald-700">
                                            {rc.label} in {data.name}
                                        </div>
                                        <div className="text-xs text-slate-600 leading-relaxed">{rc.tagline}</div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Batch 2N — City AEO Conversational FAQ */}
            <ConversationalFAQ
                heading={`Common questions ${data.name} parents ask us`}
                subheading="Direct, no-filler answers to the questions Google's AI Overviews most often need — matched to your board and neighbourhood."
                items={cityAeo.faqs}
                testId={`city-conversational-faq-${data.slug}`}
            />

            {/* Boards */}
            <section className="py-16 bg-[#F8FAFC]">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#3B82F6] mb-3">All Boards in {data.name}</div>
                    <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-8">Specialist online tutors for every curriculum</h2>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {BOARDS.map((b) => (
                            <div key={b.code} className="bg-white rounded-xl border border-slate-200 p-5">
                                <h3 className="font-heading font-semibold text-[#1E3A8A] mb-1">{b.name} online tutor in {data.name}</h3>
                                <p className="text-xs text-slate-500 leading-relaxed">{b.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Why us */}
            <section className="py-16 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#3B82F6] mb-3">Why {data.name} Parents Choose Us</div>
                    <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-8">Online tutor quality you can verify.</h2>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {[
                            [GraduationCap, "Verified online tutor pool", `Every ${data.name} online tutor is interviewed, demo-tested and background-verified. Less than 12% of applicants make it into our network.`],
                            [Video, "True 1:1 — never group", "One student, one tutor, one personalised plan. Live two-way HD video with digital whiteboard and screen-share."],
                            [ShieldCheck, "Free tutor replacement", "If you're not satisfied, we rematch the tutor at no cost — no questions asked."],
                            [Clock, "Flexible scheduling", "Morning, evening or weekend slots. Reschedule with 4 hours notice."],
                            [Award, "Weekly progress tracking", "Sunday parent reports — what was covered, homework, next week's plan."],
                            [Users, "Dedicated coordinator", "One named coordinator handles all communications — no call-centre runaround."],
                        ].map(([I, t, d]) => (
                            <div key={t} className="p-6 bg-[#F8FAFC] rounded-2xl border border-slate-200">
                                <div className="w-12 h-12 rounded-xl bg-blue-50 text-[#1E3A8A] flex items-center justify-center mb-3">
                                    <I className="w-6 h-6" />
                                </div>
                                <div className="font-heading font-semibold text-[#0F172A] mb-1">{t}</div>
                                <div className="text-sm text-slate-600 leading-relaxed">{d}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Testimonial */}
            <section className="py-16 bg-[#0F172A] text-white relative overflow-hidden">
                <div className="absolute inset-0 bg-grid opacity-10"></div>
                <div className="relative max-w-3xl mx-auto px-6 lg:px-8 text-center">
                    <div className="flex justify-center gap-1 mb-4 text-amber-400">{Array.from({length:5}).map((_,i)=> <Star key={i} className="w-5 h-5 fill-amber-400" />)}</div>
                    <p className="font-heading text-xl sm:text-2xl leading-relaxed text-blue-100">"{data.testimonial.t}"</p>
                    <div className="mt-6">
                        <div className="font-heading font-semibold">{data.testimonial.n}</div>
                        <div className="text-xs text-blue-300 uppercase tracking-[0.2em] mt-1">{data.testimonial.c}</div>
                    </div>
                </div>
            </section>

            {/* FAQ + Registration */}
            <section className="py-16 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-5 gap-12">
                    <div className="lg:col-span-3">
                        <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#3B82F6] mb-3">{data.name} FAQs</div>
                        <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-8">Common questions from {data.name} parents</h2>
                        <div className="space-y-3">
                            {data.faqs.map(([q, a]) => (
                                <details key={q} className="group bg-[#F8FAFC] border border-slate-200 rounded-xl p-5 [&_summary::-webkit-details-marker]:hidden">
                                    <summary className="cursor-pointer font-heading font-semibold text-[#0F172A] flex items-center justify-between">
                                        {q}
                                        <span className="text-[#3B82F6] text-2xl group-open:rotate-45 transition-transform">+</span>
                                    </summary>
                                    <p className="mt-3 text-sm text-slate-600 leading-relaxed">{a}</p>
                                </details>
                            ))}
                        </div>
                        <div className="mt-8 flex flex-wrap gap-3 text-sm">
                            <Link to="/online-classes" className="inline-flex items-center gap-1 text-[#1E3A8A] font-semibold hover:gap-2 transition-all">1:1 Online Classes <ArrowRight className="w-4 h-4" /></Link>
                            <span className="text-slate-300">·</span>
                            <Link to="/foreign-students" className="inline-flex items-center gap-1 text-[#1E3A8A] font-semibold hover:gap-2 transition-all">For NRI students <ArrowRight className="w-4 h-4" /></Link>
                            <span className="text-slate-300">·</span>
                            <Link to="/courses" className="inline-flex items-center gap-1 text-[#1E3A8A] font-semibold hover:gap-2 transition-all">All courses <ArrowRight className="w-4 h-4" /></Link>
                            <span className="text-slate-300">·</span>
                            <Link to="/blog" className="inline-flex items-center gap-1 text-[#1E3A8A] font-semibold hover:gap-2 transition-all">Tutoring blog <ArrowRight className="w-4 h-4" /></Link>
                        </div>
                    </div>
                    <div className="lg:col-span-2" id="book-demo">
                        <RegistrationForm title={`Book Free Online Demo · ${data.name}`} defaultCity={data.name} />
                    </div>
                </div>
            </section>

            {/* Other cities (internal linking) */}
            <section className="py-12 bg-[#F8FAFC] border-t border-slate-200">
                <div className="max-w-6xl mx-auto px-6 lg:px-8 text-center">
                    <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#3B82F6] mb-2">Also Available In</div>
                    <h3 className="font-heading text-xl font-semibold mb-4">Anand Tutorials 1:1 online classes in other cities</h3>
                    <div className="flex flex-wrap justify-center gap-2">
                        {Object.values(ALL_CITIES).filter((c) => c.slug !== data.slug).slice(0, 14).map((c) => (
                            <Link key={c.slug} to={`/${c.urlSlug}`} className="px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-semibold text-[#1E3A8A] hover:border-blue-200 hover:shadow-sm">
                                Online tuition in {c.name} →
                            </Link>
                        ))}
                        <Link to="/online-tuition-worldwide" className="px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-semibold text-[#1E3A8A] hover:border-blue-200 hover:shadow-sm">🌍 Worldwide →</Link>
                        <Link to="/jabalpur" className="px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-semibold text-[#1E3A8A] hover:border-blue-200 hover:shadow-sm">Home Tuition · Jabalpur →</Link>
                        <Link to="/bhopal" className="px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-semibold text-[#1E3A8A] hover:border-blue-200 hover:shadow-sm">Bhopal →</Link>
                        <Link to="/nagpur" className="px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-semibold text-[#1E3A8A] hover:border-blue-200 hover:shadow-sm">Nagpur →</Link>
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="py-16 bg-gradient-to-r from-[#1E3A8A] to-[#1E40AF] text-white">
                <div className="max-w-5xl mx-auto px-6 lg:px-8 text-center">
                    <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-3">Start your child's success story in {data.name}.</h2>
                    <p className="text-blue-100 max-w-2xl mx-auto mb-6">Free 1:1 live online demo with a verified tutor. No payment, no commitment.</p>
                    <div className="flex flex-wrap justify-center gap-3">
                        <a href={`tel:${ENQUIRY_PHONE_RAW}`} className="inline-flex items-center gap-2 bg-amber-500 text-[#0F172A] font-semibold px-6 py-3 rounded-lg hover:bg-amber-400">
                            <Phone className="w-4 h-4" /> Call India Team
                        </a>
                        <Link to="/register" className="inline-flex items-center gap-2 bg-white text-[#1E3A8A] font-semibold px-6 py-3 rounded-lg hover:bg-slate-50">
                            Book Free Demo
                        </Link>
                        <a href={`https://wa.me/${SOCIAL.whatsapp}?text=${encodeURIComponent(`Hi Anand Tutorials, I am from ${data.name} and would like to enquire.`)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-[#25D366] text-white font-semibold px-6 py-3 rounded-lg hover:bg-[#1ebe5a]">
                            <MessageCircle className="w-4 h-4" /> WhatsApp
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
}
